import axios from 'axios'

import { FetchResult, FetchResultType } from '@/entities/FetchResult'
import { NotificationCenterHeaderName, NotificationCenterLog } from '@/entities/NotificationCenter/NotificationCenter'
import { authorizedFetch } from '@/helpers/authorizedFetch'
import { formatDate } from '@/helpers/date'
import { trans } from '@/helpers/trans'
import { generateApiUrl } from '@/helpers/url'
import { isResourceDocument } from '@/japi/guards/isResourceDocument'
import { JapiDocument } from '@/japi/types/Document'
import { ShiftPlanningResource, ShiftResource, ShiftViewModel } from '@/models'
import { transformShiftResource } from '@/services/ShiftService/transformShiftResource'
import { DateFormat } from '@/types'

export async function getShifts(
    users: string[] = [],
    departments: string[] = [],
    startDate: Date,
    endDate: Date,
    includes: string[] = [],
): Promise<ShiftViewModel[]> {
    const response = await axios.get<ShiftResource[]>(
        `${process.env.REACT_APP_API_URL}/me/shifts/open`,
        {
            params: {
                userUuids: users.length > 0 ? JSON.stringify(users) : undefined,
                startDate: formatDate(startDate, DateFormat.apiDateTime),
                endDate: formatDate(endDate, DateFormat.apiDateTime),
                departments: departments.length > 0 ? JSON.stringify(departments) : undefined,
                include: includes.join(','),
            },
        },
    )

    return response.data
        .map((shift) => transformShiftResource(
            shift,
            shift.shiftPlannings,
            shift.tracks,
            shift.department,
        ))
}

export async function getShift(id: string, includes: string[] = []): Promise<ShiftViewModel> {
    const response = await axios.get<ShiftResource>(
        `${process.env.REACT_APP_API_URL}/shifts/${id}`,
        {
            params: {
                include: includes.join(','),
            },
        },
    )

    return transformShiftResource(
        response.data,
        response.data.shiftPlannings,
        response.data.tracks,
        response.data.department,
    )
}

export const registerShift = async (
    shiftId: string,
    eventLogTrackerData: NotificationCenterLog,
): Promise<FetchResult<string, string>> => {
    try {
        const url = generateApiUrl({
            endpoint: `/me/shift/register/${shiftId}`,
        })

        const response = await authorizedFetch(url, {
            method: 'POST',
            ...eventLogTrackerData && {
                headers: {
                    [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
                },
            },
        })

        const doc: JapiDocument = await response.json()

        if (! isResourceDocument<ShiftPlanningResource>(doc) || ! doc.data) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: doc.errors[0]?.detail || trans('errors.unknownError'),
            }
        }

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: shiftId,
        }
    } catch (error) {
        console.error('[registerShift]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const unregisterShift = async (
    shiftId: string,
    eventLogTrackerData: NotificationCenterLog,
): Promise<FetchResult<boolean, string>> => {
    try {
        const url = generateApiUrl({
            endpoint: `/me/shift/register/${shiftId}`,
        })

        const response = await authorizedFetch(url, {
            method: 'DELETE',
            ...eventLogTrackerData && {
                headers: {
                    [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
                },
            },
        })

        if (! response.ok) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        return {
            status: 200,
            type: FetchResultType.Success,
            data: true,
        }
    } catch (error) {
        console.error('[unregisterShift]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const getShiftById = (includes: string[] = [], shiftUuid?: string) => axios.get(
    `${process.env.REACT_APP_API_URL}/shifts/${shiftUuid}`,
    {
        params: {
            include: includes.join(','),
        },
    },
)
